// File: src/components/admin/scheduler/SchedulerManagementView.js
// Main admin view for managing the scheduler system

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Snackbar,
    Alert,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    RestartAlt as RestartAltIcon,
    MergeType as MergeTypeIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';
import SchedulerStatsCards from './SchedulerStatsCards';
import SchedulerHealthPanel from './SchedulerHealthPanel';
import SchedulerUsersTable from './SchedulerUsersTable';
import SchedulerErrorsPanel from './SchedulerErrorsPanel';
import UserSchedulerDetailDialog from './UserSchedulerDetailDialog';

const SchedulerManagementView = () => {
    // Data state
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [errors, setErrors] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });

    // UI state
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [errorsExpanded, setErrorsExpanded] = useState(true);
    const [selectedUserUid, setSelectedUserUid] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);

    // Bulk reset state
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [resetting, setResetting] = useState(false);

    // Migration state
    const [migrateDialogOpen, setMigrateDialogOpen] = useState(false);
    const [migrating, setMigrating] = useState(false);

    // Snackbar state
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Fetch stats
    const fetchStats = useCallback(async () => {
        try {
            const result = await adminApi.getSchedulerStats({ days: 7 });
            setStats(result);
        } catch (err) {
            console.error('Failed to fetch scheduler stats:', err);
        }
    }, []);

    // Fetch users
    const fetchUsers = useCallback(async (page = 1, pageSize = 20) => {
        try {
            const result = await adminApi.getSchedulerUsers({ page, pageSize });
            setUsers(result.users || []);
            setPagination(result.pagination || { page, pageSize, total: 0 });
        } catch (err) {
            console.error('Failed to fetch scheduler users:', err);
        }
    }, []);

    // Fetch errors
    const fetchErrors = useCallback(async () => {
        try {
            const result = await adminApi.getSchedulerErrors({ limit: 20 });
            setErrors(result.errors || []);
        } catch (err) {
            console.error('Failed to fetch scheduler errors:', err);
        }
    }, []);

    // Initial data load
    const loadAllData = useCallback(async () => {
        setLoading(true);
        await Promise.all([
            fetchStats(),
            fetchUsers(pagination.page, pagination.pageSize),
            fetchErrors(),
        ]);
        setLoading(false);
    }, [fetchStats, fetchUsers, fetchErrors, pagination.page, pagination.pageSize]);

    useEffect(() => {
        loadAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle refresh
    const handleRefresh = () => {
        loadAllData();
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        fetchUsers(newPage, pagination.pageSize);
    };

    // Handle rows per page change
    const handleRowsPerPageChange = (newPageSize) => {
        fetchUsers(1, newPageSize);
    };

    // Handle view user
    const handleViewUser = (uid) => {
        setSelectedUserUid(uid);
        setDetailDialogOpen(true);
    };

    // Handle pause user
    const handlePauseUser = async (uid) => {
        try {
            await adminApi.pauseUserSchedules(uid);
            setSnackbar({
                open: true,
                message: 'All schedules paused successfully',
                severity: 'success',
            });
            fetchUsers(pagination.page, pagination.pageSize);
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({
                open: true,
                message: `Failed to pause: ${message}`,
                severity: 'error',
            });
        }
    };

    // Handle resume user
    const handleResumeUser = async (uid) => {
        try {
            await adminApi.resumeUserSchedules(uid);
            setSnackbar({
                open: true,
                message: 'All schedules resumed successfully',
                severity: 'success',
            });
            fetchUsers(pagination.page, pagination.pageSize);
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({
                open: true,
                message: `Failed to resume: ${message}`,
                severity: 'error',
            });
        }
    };

    // Handle detail dialog close
    const handleDetailDialogClose = () => {
        setDetailDialogOpen(false);
        setSelectedUserUid(null);
    };

    // Handle bulk reset circuit breakers
    const handleBulkResetCircuitBreakers = async () => {
        setResetting(true);
        try {
            const result = await adminApi.resetAllCircuitBreakers();
            setSnackbar({
                open: true,
                message: result.message || `Reset ${result.resetCount} circuit-broken schedule(s)`,
                severity: result.resetCount > 0 ? 'success' : 'info',
            });
            setResetDialogOpen(false);
            loadAllData();
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({
                open: true,
                message: `Failed to reset circuit breakers: ${message}`,
                severity: 'error',
            });
        } finally {
            setResetting(false);
        }
    };

    // Handle migrate stats refresh
    const handleMigrateStatsRefresh = async () => {
        setMigrating(true);
        try {
            const result = await adminApi.migrateStatsRefresh();
            setSnackbar({
                open: true,
                message: result.message || `Migrated ${result.migrated} schedule(s)`,
                severity: result.migrated > 0 ? 'success' : 'info',
            });
            setMigrateDialogOpen(false);
            loadAllData();
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({
                open: true,
                message: `Failed to migrate: ${message}`,
                severity: 'error',
            });
        } finally {
            setMigrating(false);
        }
    };

    // Handle snackbar close
    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <Box data-testid="scheduler-management-view">
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" fontWeight={600}>
                    Scheduler Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        color="info"
                        startIcon={<MergeTypeIcon />}
                        onClick={() => setMigrateDialogOpen(true)}
                        disabled={loading || migrating}
                    >
                        Migrate Stats Refresh
                    </Button>
                    <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<RestartAltIcon />}
                        onClick={() => setResetDialogOpen(true)}
                        disabled={loading || resetting}
                    >
                        Reset All Circuit Breakers
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Stats Cards */}
            <SchedulerStatsCards stats={stats} loading={loading} />

            {/* Health Check Panel */}
            <SchedulerHealthPanel onViewUser={handleViewUser} />

            {/* Errors Panel */}
            <SchedulerErrorsPanel
                errors={errors}
                loading={loading}
                expanded={errorsExpanded}
                onToggleExpand={() => setErrorsExpanded(!errorsExpanded)}
                onViewUser={handleViewUser}
            />

            {/* Users Table */}
            <SchedulerUsersTable
                users={users}
                loading={loading}
                pagination={pagination}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onViewUser={handleViewUser}
                onPauseUser={handlePauseUser}
                onResumeUser={handleResumeUser}
            />

            {/* User Detail Dialog */}
            <UserSchedulerDetailDialog
                open={detailDialogOpen}
                onClose={handleDetailDialogClose}
                userUid={selectedUserUid}
                onRefresh={() => fetchUsers(pagination.page, pagination.pageSize)}
            />

            {/* Bulk Reset Confirmation Dialog */}
            <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
                <DialogTitle>Reset All Circuit Breakers</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will re-enable all schedules that were disabled by the circuit breaker
                        (5 consecutive failures) across all users. The failure counters will be reset to zero.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetDialogOpen(false)} disabled={resetting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleBulkResetCircuitBreakers}
                        color="warning"
                        variant="contained"
                        disabled={resetting}
                    >
                        {resetting ? 'Resetting...' : 'Reset All'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Migrate Stats Refresh Confirmation Dialog */}
            <Dialog open={migrateDialogOpen} onClose={() => setMigrateDialogOpen(false)}>
                <DialogTitle>Migrate Stats Refresh Schedules</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will merge all existing STATS_REFRESH schedules into their corresponding
                        PLATFORM_SYNC schedules by enabling the &quot;includeStatsRefresh&quot; flag.
                        The old STATS_REFRESH schedules will be deleted. Stats will continue to refresh
                        automatically after each platform sync.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMigrateDialogOpen(false)} disabled={migrating}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleMigrateStatsRefresh}
                        color="info"
                        variant="contained"
                        disabled={migrating}
                    >
                        {migrating ? 'Migrating...' : 'Migrate All'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SchedulerManagementView;
